/**
 * 형태소 표의 rowSpan 계산.
 *
 * 의미(WSD) 상세는 표를 넓히지 않기 위해 **별도 행**으로 펼친다. 그러면 번호·어절
 * 셀은 형태소 행뿐 아니라 끼어든 상세 행까지 덮어야 하므로 rowSpan 이 달라진다.
 * 이 계산이 틀리면 표가 어긋나므로(칸이 밀리거나 테두리가 깨진다) 순수 함수로
 * 떼어내 불변식을 테스트한다.
 *
 * 인덱스는 모두 "한 문장 안 형태소 행"의 0-기반 인덱스다.
 */

/** [start, end) 구간에서 펼쳐진 행의 개수. */
export function countExpandedInRange(
  expanded: Set<number>,
  start: number,
  end: number
): number {
  let count = 0;
  for (let i = start; i < end; i++) {
    if (expanded.has(i)) count++;
  }
  return count;
}

/**
 * 실제로 렌더되는 행 수 = 형태소 행 + 펼쳐진 상세 행.
 * 번호 셀의 rowSpan 이 이 값과 같아야 한다.
 */
export function totalRenderedRows(
  morphemeRows: number,
  expandedRows: number
): number {
  return morphemeRows + expandedRows;
}

export interface PhraseSpan {
  /** 어절이 시작되는 형태소 행 인덱스 */
  startIdx: number;
  /** 이 어절에 속한 형태소 행 수 */
  morphemeRows: number;
  /** 그중 펼쳐진 상세 행 수 */
  expandedRows: number;
  /** 어절 셀에 줘야 하는 rowSpan */
  rowSpan: number;
}

/**
 * 어절(ptext 가 같은 연속 구간)별 rowSpan.
 *
 * 종전 표는 ptext 가 같은 연속 행을 하나의 어절 셀로 묶었다(getRowSpan). 여기서는
 * 그 구간에 끼어든 상세 행까지 더한다.
 */
export function phraseSpans(
  ptexts: string[],
  expanded: Set<number> = new Set()
): PhraseSpan[] {
  const spans: PhraseSpan[] = [];
  let i = 0;
  while (i < ptexts.length) {
    let morphemeRows = 1;
    while (
      i + morphemeRows < ptexts.length &&
      ptexts[i + morphemeRows] === ptexts[i]
    ) {
      morphemeRows++;
    }
    const expandedRows = countExpandedInRange(expanded, i, i + morphemeRows);
    spans.push({
      startIdx: i,
      morphemeRows,
      expandedRows,
      rowSpan: morphemeRows + expandedRows
    });
    i += morphemeRows;
  }
  return spans;
}
